import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import {
  FaDownload,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaEye
} from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useScreenPermissions } from '../components/useScreenPermissions';
import FeederViewModal from './FeederViewModal'; // Import the View Modal

const API_URL = '/api';

// Colors
const COLORS = {
  ANGEL: '#EAF9FB',
  THALASSA: '#D5F0F5',
  TABLE_BODY: '#FFFFFF',
  SECTION_TITLE_BG: '#FFFFFF',
  SECTION_TITLE_TEXT: '#0f2b36',
  HEADER_TEXT_WHITE: '#ffffffff',
  DIVIDER: '#E6EEF2',
};

const FeederList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [feeders, setFeeders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeeder, setSelectedFeeder] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [skippedFeeders, setSkippedFeeders] = useState([]); // <-- NEW
  const [showSkippedDetails, setShowSkippedDetails] = useState(false); // <-- NEW
  
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // Stats state
  const [stats, setStats] = useState({
    totalFeeders: 0,
    activeFeeders: 0,
    inactiveFeeders: 0,
    createdThisMonth: 0,
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // Sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);

  // Fetch feeders
  useEffect(() => {
    fetchFeeders();
  }, []);

  const fetchFeeders = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/feeder-filter`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch feeders');

      const data = await response.json();
      setFeeders(data);
      calculateStats(data);
    } catch (error) {
      console.error('Error fetching feeders:', error);
      alert('Error loading feeders');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (feederList) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let totalFeeders = feederList.length;
    let activeFeeders = 0;
    let inactiveFeeders = 0;
    let createdThisMonth = 0;

    feederList.forEach((f) => {
      const isActive = f.Active === 1 || f.Active === true;
      if (isActive) activeFeeders++;
      else inactiveFeeders++;

      const date = new Date(f.CreatedAt);
      if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
        createdThisMonth++;
      }
    });

    setStats({
      totalFeeders,
      activeFeeders,
      inactiveFeeders,
      createdThisMonth,
    });
  };

  const handleDownloadCSV = () => {
    let csvContent = 'Feeder Code,Description,SubStation,Circle,District,City,Status\n';

    feeders.forEach((f) => {
      const isActive = f.Active === 1 || f.Active === true;
      const status = isActive ? 'Active' : 'Inactive';
      csvContent += `${f.Code},"${f.Description || ''}","${f.SubStation || ''}","${f.Circle || ''}","${f.District || ''}","${f.City || ''}",${status}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'feeders.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSync = async () => {
  if (isSyncing) return;
  setIsSyncing(true);
  
  try {
    const res = await fetch(`${API_URL}/mapping/bc-feeders/sync`, { method: 'GET' ,headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    const data = await res.json();

    if (data.success) {
      setSkippedFeeders(data.skipped || []); // store skipped items
      setShowSyncSuccess(true);
      fetchFeeders();
    } else {
      alert(`Sync Failed: ${data.message || 'Unknown error'}`);
    }
  } catch (error) {
    console.error("Sync Error:", error);
    alert("An error occurred while syncing feeders.");
  } finally {
    setIsSyncing(false);
  }
};

  const handleView = (feeder) => {
    setSelectedFeeder(feeder.Code);
    setViewModalOpen(true);
  };

  const handleLogout = () => {
    navigate('/login');
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredFeeders = feeders.filter((f) => {
    const matchesSearch =
      f.Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f.Description && f.Description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (f.SubStation && f.SubStation.toLowerCase().includes(searchTerm.toLowerCase()));

    const isActive = f.Active === 1 || f.Active === true;
    const matchesStatus = status === 'All' || 
                          (status === 'Active' && isActive) || 
                          (status === 'Inactive' && !isActive);
    return matchesSearch && matchesStatus;
  });

  const sortedFeeders = getSortedData(filteredFeeders);
  const totalPages = Math.ceil(sortedFeeders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedFeeders.slice(startIndex, endIndex);

  const renderSortArrows = (columnName) => {
    const isSorted = sortConfig.key === columnName;
    const isAsc = isSorted && sortConfig.direction === 'asc';
    const isDesc = isSorted && sortConfig.direction === 'desc';

    return (
      <span className="ml-2 flex flex-col">
        <FaSortUp className={`h-3 w-3 ${isAsc ? 'text-slate-800' : 'text-slate-400'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1.5 ${isDesc ? 'text-slate-800' : 'text-slate-400'}`} />
      </span>
    );
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    buttons.push(
      <button key="prev" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}
        className={`p-2 transition-all ${currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'} rounded-none`}>
        <FaChevronLeft className="h-5 w-5" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button key={i} onClick={() => setCurrentPage(i)}
          className={`px-4 py-1 font-medium transition-all ${i === currentPage ? 'bg-blue-500 text-white rounded-none' : 'text-slate-800 hover:bg-slate-100 rounded-none'}`}>
          {i}
        </button>
      );
    }

    buttons.push(
      <button key="next" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'} rounded-none`}>
        <FaChevronRight className="h-5 w-5" />
      </button>
    );

    return buttons;
  };

  const canView = hasFullAccess || hasPermission('FEEDER', 'CanView');

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: COLORS.ANGEL }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-slate-800 mx-auto rounded-full"></div>
          <p className="mt-4 text-lg font-medium text-slate-700">Loading permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: COLORS.ANGEL }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={{ activeBg: '#4f8ef5ff', activeText: '#ffffffff', profileBg: '#4f8ef5ff' }} onLogout={handleLogout} />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        <header>
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-dark-500 mb-2">Feeders</h1>
                <p className="text-gray-600">Manage feeder details here.</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handleSync} disabled={isSyncing}
                  className={`flex items-center gap-2 px-4 py-2 rounded-none font-medium transition-all duration-200 border ${isSyncing ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300' : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'}`}>
                  {isSyncing ? (<>Syncing...</>) : (<>Sync</>)}
                </button>
                <button onClick={handleDownloadCSV} className="flex items-center gap-2 rounded-none border border-slate-200 px-3 py-2 bg-white text-slate-800 hover:bg-slate-50 transition-all duration-200"><FaDownload className="w-4 h-4" />Export</button>
              </div>
            </div>
            <div style={{ height: 1, backgroundColor: COLORS.DIVIDER, marginTop: 16, marginBottom: 20 }} />
          </div>
        </header>

        {/* <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Feeders', value: stats.totalFeeders, color: 'bg-slate-100' },
            { label: 'Active Feeders', value: stats.activeFeeders, color: 'bg-teal-50' },
            { label: 'Inactive Feeders', value: stats.inactiveFeeders, color: 'bg-amber-50' },
            { label: 'Created This Month', value: stats.createdThisMonth, color: 'bg-slate-100' },
          ].map((stat, i) => (
            <div key={i} className="p-5 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 rounded-none">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium text-slate-600">{stat.label}</p><p className="text-2xl font-semibold text-slate-900">{stat.value}</p></div>
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${stat.color} text-slate-800`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg></div>
              </div>
            </div>
          ))}
        </section> */}

        <section className="p-5 bg-white border border-slate-200 shadow-sm mb-6 rounded-none">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
              <input type="text" placeholder="Search Code, Description, Substation..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-4 py-2 bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none" />
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-4 py-2 bg-white border border-slate-200 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all duration-200 rounded-none">
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div> */}
            <button className="px-5 py-2 bg-blue-500 text-white font-medium hover:bg-blue-400 transition-all duration-200 shadow rounded-none" onClick={() => fetchFeeders()}>Search</button>
          </div>
        </section>

        <section className="shadow-sm overflow-hidden rounded-none border border-slate-200">
          <div className="flex justify-between items-center p-4" style={{ backgroundColor: COLORS.SECTION_TITLE_BG, borderBottom: `1px solid ${COLORS.DIVIDER}` }}>
            <h2 className="text-xl font-semibold" style={{ color: COLORS.SECTION_TITLE_TEXT }}>Feeders</h2>
            <div className="flex gap-2">{renderPaginationButtons()}</div>
          </div>

          {loading ? (<div className="p-8 text-center text-slate-600">Loading feeders...</div>) : sortedFeeders.length === 0 ? (<div className="p-8 text-center text-slate-600">No feeders found</div>) : (
            <div className="overflow-x-auto" style={{ backgroundColor: COLORS.TABLE_BODY }}>
              <table className="w-full text-sm" style={{ backgroundColor: COLORS.TABLE_BODY }}>
                <thead style={{ backgroundColor: COLORS.THALASSA }} className="text-gray-600 text-xs text-left font-semibold uppercase">
                  <tr>
                    <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort('Code')}> <div className="flex items-center">Feeder Code {renderSortArrows('Code')}</div></th>
                    <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort('Description')}>
                        <div className="flex items-center">Description {renderSortArrows('Description')}</div>
                    </th>
                    <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort('SubStation')}>
                        <div className="flex items-center">Sub Station {renderSortArrows('SubStation')}</div>
                    </th>
                    <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort('Circle')}>
                        <div className="flex items-center">Circle {renderSortArrows('Circle')}</div>
                    </th>
                    <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort('City')}>
                        <div className="flex items-center">City {renderSortArrows('City')}</div>
                    </th>
                    <th className="px-6 py-3 text-left cursor-pointer" onClick={() => handleSort('ProjectNo')}>
                        <div className="flex items-center">Project No. {renderSortArrows('ProjectNo')}</div>
                    </th>
                    <th className="px-6 py-3 text-center text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: COLORS.DIVIDER }}>
                  {currentItems.map((f) => {
                    const isActive = f.Active === 1 || f.Active === true;
                    const statusText = isActive ? 'Active' : 'Inactive';
                    return (
                      <tr key={f.FeederId || f.Code} className="hover:bg-white/70 transition-colors duration-200">
                        <td className="px-6 py-3 text-left font-medium text-slate-900">{f.Code}</td>
                        <td className="px-6 py-3 text-left text-slate-700">{f.Description || '-'}</td>
                        <td className="px-6 py-3 text-left text-slate-700">{f.SubStation || '-'}</td>
                        <td className="px-6 py-3 text-left text-slate-700">{f.Circle || '-'}</td>
                        <td className="px-6 py-3 text-left text-slate-700">{f.City || '-'}</td>
                        <td className="px-6 py-3"><span className="px-6 py-3 text-left text-slate-700">{f.ProjectNo}</span></td>
                        <td className="px-6 py-3"><div className="flex gap-3 justify-center">{canView && (<button onClick={() => handleView(f)} className="text-slate-800 hover:text-slate-900" title="View"><FaEye className="h-4 w-4" /></button>)}</div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <FeederViewModal open={viewModalOpen} onClose={() => setViewModalOpen(false)} feederId={selectedFeeder} />

        {showSyncSuccess && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 text-center transform transition-all scale-100">
      <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
        <FaCheck className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">Sync Complete!</h3>
      <p className="text-gray-500 mb-4">
        Feeder data has been synchronized successfully.
      </p>

      {skippedFeeders.length > 0 && (
        <div className="text-left mb-4">
          <button
            onClick={() => setShowSkippedDetails((prev) => !prev)}
            className="text-blue-600 underline mb-2"
          >
            {showSkippedDetails ? 'Hide' : 'View'} Skipped Feeders ({skippedFeeders.length})
          </button>

          {showSkippedDetails && (
            <div className="max-h-64 overflow-y-auto border border-gray-200 p-2 rounded">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1 text-left">Code</th>
                    <th className="px-2 py-1 text-left">ProjectNo</th>
                    <th className="px-2 py-1 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {skippedFeeders.map((f, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-2 py-1">{f.code}</td>
                      <td className="px-2 py-1">{f.projectNo || '-'}</td>
                      <td className="px-2 py-1 text-red-600">{f.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => {
          setShowSyncSuccess(false);
          setShowSkippedDetails(false);
          setSkippedFeeders([]);
        }}
        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 shadow-sm hover:bg-blue-400 focus:outline-none transition-all duration-200"
      >
        Close
      </button>
    </div>
  </div>
)}

      </main>
    </div>
  );
};

export default FeederList;